import {
    motion,
    useMotionValue,
    useTransform,
    animate,
    useMotionValueEvent,
} from "framer-motion";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { AppText } from "@/components/Text/AppText";
import { getScoreHexColor } from "@/utils/scoreColors";

interface ScoreGaugeProps {
    score: number;
    maxScore?: number;
    className?: string;
    loading?: boolean;
}

export const ScoreGauge = ({
    score,
    maxScore = 100,
    className = "",
    loading = true,
}: ScoreGaugeProps) => {
    const scoreMotionValue = useMotionValue(0);
    const animatedScore = useTransform(scoreMotionValue, value =>
        Math.round(value),
    );
    const [displayScore, setDisplayScore] = useState(0);

    useMotionValueEvent(animatedScore, "change", latest => {
        setDisplayScore(latest);
    });

    const percentage = Math.min(Math.max(score / maxScore, 0), 1);
    const radius = 60;
    const strokeWidth = 12;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - percentage * circumference;

    const scoreColor = loading ? "#C8CED4" : getScoreHexColor(displayScore);

    useEffect(() => {
        if (!loading) {
            const animation = animate(scoreMotionValue, score, {
                duration: 1.2,
                ease: "easeOut",
            });

            return animation.stop;
        }
    }, [score, scoreMotionValue, loading]);

    return (
        <div className={`relative h-48 w-48 ${className}`}>
            {/* Outer glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg" />

            <svg
                className="h-full w-full -rotate-90 transform drop-shadow-sm"
                viewBox={`0 0 ${radius * 2 + strokeWidth} ${radius * 2 + strokeWidth}`}
            >
                {/* Background circle with gradient */}
                <defs>
                    <linearGradient
                        id="backgroundGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop
                            offset="0%"
                            stopColor="#f3f4f6"
                        />
                        <stop
                            offset="100%"
                            stopColor="#e5e7eb"
                        />
                    </linearGradient>
                    <linearGradient
                        id="progressGradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop
                            offset="0%"
                            stopColor={scoreColor}
                        />
                        <stop
                            offset="100%"
                            stopColor={scoreColor}
                            stopOpacity="0.8"
                        />
                    </linearGradient>
                </defs>

                {/* Background circle */}
                <circle
                    cx={radius + strokeWidth / 2}
                    cy={radius + strokeWidth / 2}
                    r={radius}
                    stroke="url(#backgroundGradient)"
                    strokeWidth={strokeWidth}
                    fill="none"
                    opacity="0.6"
                />

                {/* Progress circle */}
                <motion.circle
                    cx={radius + strokeWidth / 2}
                    cy={radius + strokeWidth / 2}
                    r={radius}
                    stroke="url(#progressGradient)"
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                />
            </svg>

            {/* Score text with enhanced styling */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    className="flex flex-col items-center gap-1"
                >
                    {loading ? (
                        <>
                            <LoadingSpinner size={32} />
                            <AppText
                                variant="labelsbuttons"
                                className="text-gray-600 drop-shadow-sm"
                            >
                                Scanning
                            </AppText>
                        </>
                    ) : (
                        <>
                            {displayScore > 0 && (
                                <AppText
                                    variant="headings"
                                    className="drop-shadow-sm"
                                    style={{ color: scoreColor }}
                                >
                                    {displayScore}%
                                </AppText>
                            )}
                            <AppText
                                variant="labelsbuttons"
                                className="drop-shadow-sm"
                                style={{ color: scoreColor }}
                            >
                                {displayScore > 0
                                    ? "Resume Score"
                                    : "Ready to Scan"}
                            </AppText>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
};
